"use client";

import { motion } from "framer-motion";
import { Euro, Home, ShieldCheck, Ticket, Utensils, Wallet } from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import type { University } from "@/lib/universities";
import { costOfLiving, type UniversityDetail } from "@/lib/university-details";

export default function FeeSection({
  university,
  detail,
}: {
  university: University;
  detail: UniversityDetail;
}) {
  const living = costOfLiving[detail.costTier];

  const costs = [
    { icon: Home, label: `Miete (${living.label.toLowerCase()})`, value: living.rent },
    { icon: Utensils, label: "Ernährung", value: "ca. 200–300 € / Monat" },
    { icon: ShieldCheck, label: "Krankenversicherung", value: "ca. 130 € / Monat" },
    { icon: Wallet, label: "Freizeit & Sonstiges", value: "ca. 150–250 € / Monat" },
  ];

  return (
    <motion.section
      variants={stagger(0.09)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-labelledby="fees-heading"
    >
      <motion.p
        variants={fadeUp}
        className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-widest text-accent mb-3"
      >
        <Euro size={14} aria-hidden="true" />
        Kosten
      </motion.p>
      <motion.h2
        variants={fadeUp}
        id="fees-heading"
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-6"
      >
        Studiengebühren &amp; Lebenshaltungskosten
      </motion.h2>

      {/* Gebühren */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <motion.div
          variants={fadeUp}
          className="card-flat flex flex-col gap-1.5 bg-white/60 p-6"
        >
          <span className="text-xs font-bold uppercase tracking-wider text-ink-faint">
            Studiengebühren
          </span>
          <span className="font-display font-bold text-2xl text-accent">
            {university.tuition ?? "Keine"}
          </span>
          <p className="text-sm text-ink-soft leading-relaxed">
            {university.tuition === null
              ? "Als staatliche Hochschule erhebt die Universität keine Studiengebühren."
              : "Viele private Hochschulen bieten Stipendien oder Finanzierungsmodelle an – informiere dich direkt bei der Hochschule."}
          </p>
        </motion.div>
        <motion.div
          variants={fadeUp}
          className="card-flat flex flex-col gap-1.5 bg-white/60 p-6"
        >
          <span className="text-xs font-bold uppercase tracking-wider text-ink-faint">
            Semesterbeitrag
          </span>
          <span className="font-display font-bold text-2xl text-ink inline-flex items-center gap-2">
            <Ticket size={20} className="text-accent" aria-hidden="true" />
            ca. 150–400 €
          </span>
          <p className="text-sm text-ink-soft leading-relaxed">
            Pro Semester, je nach Hochschule – häufig ist das Semesterticket für den
            Nahverkehr bereits enthalten.
          </p>
        </motion.div>
      </div>

      {/* Lebenshaltung */}
      <motion.div
        variants={fadeUp}
        className="card-flat bg-white/60 p-6"
      >
        <h3 className="font-display font-bold text-lg text-ink mb-4">
          Leben in {university.city}
        </h3>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {costs.map((cost) => (
            <li key={cost.label} className="flex items-center justify-between gap-4 text-sm">
              <span className="inline-flex items-center gap-2.5 text-ink-soft">
                <cost.icon size={15} className="text-accent shrink-0" aria-hidden="true" />
                {cost.label}
              </span>
              <span className="font-semibold text-ink whitespace-nowrap tnum">{cost.value}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-faint leading-relaxed mt-4">
          Richtwerte zur Orientierung – die tatsächlichen Kosten hängen von Wohnform
          und Lebensstil ab.
        </p>
      </motion.div>
    </motion.section>
  );
}
